import React from 'react'
import styled, { css, keyframes } from 'styled-components'
import {
  useProductState,
  useProductFunctions,
  useConfigState,
  useLocalLabels,
} from '@sentecacommerce-theme/lib'
import { ProductPageLabels } from '../../productPage.props'
import { LazyResponsiveImage, FormError } from '../../../Common'


export const VariantSelector = ({
  resetSize,
  selectedSizeId,
  setSize,
  sizesContainerRef,
  isSizeError,
}: {
  resetSize: () => void
  selectedSizeId: string
  setSize: (id: string) => void
  sizesContainerRef: React.MutableRefObject<HTMLDivElement | null>
  isSizeError: boolean
}) => {
  const { selectedVariant, attributeRules } = useProductState()
  const config = useConfigState()
  const labels = useLocalLabels<ProductPageLabels>()
  const {
    selectVariantByAttribute,
    getVariantByAttribute,
    getCombinationsForAttributeRule,
    selectVariantByCombination,
  } = useProductFunctions()

  const selectedColor = selectedVariant?.getAttribute(
    config.attributeKeys.color
  )

  const selectedSize = selectedVariant?.getAttribute(config.attributeKeys.size)

  const allColors = attributeRules?.[config.attributeKeys.color]?.values

  const allSizes = attributeRules?.[config.attributeKeys.size]?.values

  const selectByColor = (colorId: string) => {
    selectVariantByAttribute(colorId)
    const isSelectedSizeAvailableForThisColor = getCombinationsForAttributeRule(
      config.attributeKeys.color,
      colorId
    )?.combinations[selectedSize?.id || '']

    if (!isSelectedSizeAvailableForThisColor) resetSize()
  }

  const selectBySize = (sizeId: string) => {
    setSize(sizeId)
    if (!allColors) {
      // fix bottleneck
      setTimeout(() => {
        selectVariantByAttribute(sizeId)
      })
      return
    }
    selectVariantByCombination(sizeId, selectedColor?.id as string)
  }


  const sortedSizes =
    allSizes &&
    Object.keys(allSizes).sort(
      (a, b) => allSizes[a].extra.sort - allSizes[b].extra.sort
    )

  const colorKeys = Object.keys(allColors || {})
  const sizeKeys = Object.keys(allSizes || {})

  if (!colorKeys.length && !sizeKeys.length) return null

  return (
    <Container>
      {Boolean(colorKeys.length) && (
        <VariantsContainer>
          {Object.keys(allColors || {}).map((currentColorKey) => {
            const image = getVariantByAttribute?.(currentColorKey)?.images?.[0]
            return (
              <ColorContainer
                key={currentColorKey}
                isSelected={currentColorKey === selectedColor?.id}
                onClick={() => selectByColor(currentColorKey)}
              >
                <LazyResponsiveImage
                  imagesMap={image}
                  alt={currentColorKey}
                  breakpointSizes={{}}
                  defaultImageWidth="60px"
                />
              </ColorContainer>
            )
          })}
        </VariantsContainer>
      )}
      {Boolean(sizeKeys.length) && (
        <VariantsContainer shake={isSizeError} ref={sizesContainerRef}>
          {sortedSizes.map((currentSizeKey) => {
            const size = allSizes[currentSizeKey]
            const isAvailable = getVariantByAttribute(currentSizeKey)?.inventory
              ?.hasAvailableQuantity
            return (
              <SizeContainer
                key={currentSizeKey}
                isSelected={currentSizeKey === selectedSizeId}
                isNotAvailable={!isAvailable}
                onClick={() => selectBySize(currentSizeKey)}
              >
                {size.label}
              </SizeContainer>
            )
          })}
        </VariantsContainer>
      )}
      {isSizeError && <FormError text={labels?.selectSize} />}
    </Container>
  )
}

const Container = styled.div.attrs({
  className: 'py-4',
})``

const shake = keyframes`
  10%, 90% {
    transform: translate3d(-0.1rem, 0, 0);
  }
  
  20%, 80% {
    transform: translate3d(0.2rem, 0, 0);
  }

  30%, 50%, 70% {
    transform: translate3d(-0.4rem, 0, 0);
  }

  40%, 60% {
    transform: translate3d(0.4rem, 0, 0);
  }
`

const VariantsContainer = styled.div.attrs({
  className: 'pb-1 d-flex flex-wrap flex-row',
})<{ shake?: boolean }>`
  margin: -0.8rem -0.6rem 0;
  width: calc(100% + 0.8rem);
  max-width: calc(100% + 0.8rem);
  transform: translate3d(0, 0, 0);

  &:nth-child(2) {
    padding-top: 1.2rem;
  }

  ${(props) =>
    props.shake
      ? css`
          animation: ${shake} 0.82s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
        `
      : ''}
`

const VariantBaseContainer = styled.button<{
  isDisabled?: boolean
  isSelected?: boolean
  isNotAvailable?: boolean
}>`
  pointer-events: ${(props) =>
    props.isSelected || props.isNotAvailable ? 'none' : 'all'};
  margin: 0.8rem 0.4rem 0;
  padding: 0 !important;

  ${(props) =>
    props.isNotAvailable
      ? css`
          cursor: default;
          border: none;
          background: linear-gradient(
            to right bottom,
            transparent calc(50% - 1px),
            ${({ theme }) => theme.palette.primary} calc(50% - 1px),
            ${({ theme }) => theme.palette.primary} 50%,
            transparent 50%
          );
          opacity: 0.3;
          color: ${({ theme }) => theme.palette.primary} !important;
          border-color: ${({ theme }) => theme.palette.primary} !important;
        `
      : ''}
`

const ColorContainer = styled(VariantBaseContainer).attrs({
  className: 'p-1 mr-3',
})`
  width: calc(16.666666667% - 1.2rem);
  opacity: ${(props) => (props.isSelected ? 1 : 0.4)};
`

const SizeContainer = styled(VariantBaseContainer)`
  width: calc(20% - 1.2rem);
  border: 0.1rem solid ${({ theme }) => theme.palette.silverTree};

  background-color: ${(props) =>
    props.isSelected
      ? props.theme.palette.silverTree
      : props.theme.palette.secondary};

  color: ${(props) =>
    props.isSelected
      ? props.theme.palette.secondary
      : props.theme.palette.primary};

  font-weight: ${(props) => (props.isSelected ? 'bold' : '400')};
`
